import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, Close, DeleteOutline } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDateTime } from '@campfire/hot-date';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { Badge } from '../../../../../common/badges/Badge';
import { BadgeDesign } from '../../../../../common/badges/badge-svg';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { BadgeBuilderDialog } from './badge-builder/BadgeBuilderDialog';
import {
  AchievementDraft,
  AchievementMetric,
  LevelDraft,
  METRICS,
  METRIC_OPTIONS,
  NumericReportField,
  formatThresholdList,
  isThresholdBased,
  newLevel,
  defaultBadge,
  unitLabel,
  validateDraft,
} from './achievement-model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: '23px',
      fontWeight: 550,
      color: theme.color.grey.neutral400,
      paddingLeft: '5px',
    },
    subtitle: { fontSize: '14px', paddingLeft: '5px', paddingRight: '10px' },
    content: { padding: '12px 24px 16px 24px' },
    sections: { display: 'flex', flexDirection: 'column', gap: '24px' },
    sectionLabel: { fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 },
    sectionHelp: { fontSize: '13px', color: '#808080', marginTop: '-8px', marginBottom: '12px' },
    field: { marginBottom: '16px' },
    level: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px',
      padding: '16px 0',
      borderTop: '1px solid #EFEFEF',
      [theme.breakpoints.down('sm')]: { flexWrap: 'wrap' },
    },
    levelFields: {
      flex: 1,
      display: 'grid',
      gridTemplateColumns: '1.3fr 1fr 1.3fr',
      gap: '16px',
      [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
    },
    levelMeta: { fontSize: '12px', color: '#808080', marginTop: '4px', marginLeft: '14px' },
    levelActions: { display: 'flex', alignItems: 'center', gap: '4px', minHeight: '40px' },
    noBadge: {
      width: '52px',
      height: '52px',
      borderRadius: '50%',
      border: '1px dashed #9E9E9E',
      color: '#9E9E9E',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeButton: { borderRadius: '8px', padding: '2px', '&:hover': { background: 'rgba(0,0,0,.04)' } },
    manualNote: { fontSize: '14px', color: '#808080', lineHeight: '40px' },
    switchRow: { display: 'flex', alignItems: 'flex-start', marginTop: '8px' },
    switchTitle: { fontWeight: 500, fontSize: '14px', color: '#333' },
    switchDescription: { fontSize: '13px', color: '#808080' },
    error: { color: theme.palette.error.main, fontSize: '13px', marginRight: '8px' },
  })
);

interface Props {
  open: boolean;
  initialDraft: AchievementDraft;
  reportFields: NumericReportField[];
  certificateTemplates: { certificateTemplateId: string; name: string }[];
  saving: boolean;
  onClose: () => void;
  onSave: (draft: AchievementDraft) => void;
}

const formatDate = (value: string) => unpackToDateTime(value).toFormat('d MMM y');

export const AchievementEditorDialog = ({
  open,
  initialDraft,
  reportFields,
  certificateTemplates,
  saving,
  onClose,
  onSave,
}: Props) => {
  const classes = useStyles();
  const { isMobile } = useCampfireTheme();
  const [draft, setDraft] = useState<AchievementDraft>(initialDraft);
  const [error, setError] = useState<string>();
  const [badgeLevelKey, setBadgeLevelKey] = useState<string>();
  const [removingKey, setRemovingKey] = useState<string>();

  useEffect(() => {
    if (open) {
      setDraft(initialDraft);
      setError(undefined);
    }
  }, [open, initialDraft]);

  const set = (patch: Partial<AchievementDraft>) => setDraft((prev) => ({ ...prev, ...patch }));
  const setLevel = (key: string, patch: Partial<LevelDraft>) =>
    setDraft((prev) => ({
      ...prev,
      levels: prev.levels.map((level) => (level.key === key ? { ...level, ...patch } : level)),
    }));

  const thresholdBased = isThresholdBased(draft.metric);
  const reportField = reportFields.find((field) => field.fieldId === draft.reportFieldId);
  const unit = unitLabel(draft.metric, reportField?.name.toLowerCase());
  const badgeLevel = draft.levels.find((level) => level.key === badgeLevelKey);
  const removingLevel = draft.levels.find((level) => level.key === removingKey);
  const infoColours = getAlertCardColors('info');

  const summary = useMemo(() => {
    if (!thresholdBased) return "Awarded manually by an admin from the volunteer's record.";
    const thresholds = draft.levels.map((level) => level.threshold).filter(Boolean);
    if (!thresholds.length) return 'Add a threshold to each level.';
    return `Automatically awarded to volunteers when they reach ${formatThresholdList(thresholds)}${
      unit ? ` ${unit}` : ''
    }.`;
  }, [draft.levels, thresholdBased, unit]);

  const onMetricChange = (metric: AchievementMetric) =>
    setDraft((prev) => ({
      ...prev,
      metric,
      levels: prev.levels.map((level) => ({
        ...level,
        threshold: isThresholdBased(metric) ? level.threshold : '',
        badgeDesign:
          level.badgeDesign?.centre === 'number'
            ? { ...level.badgeDesign, unit: METRICS[metric].badgeUnit }
            : level.badgeDesign,
      })),
    }));

  const onThresholdChange = (level: LevelDraft, threshold: string) => {
    const syncValue =
      level.badgeDesign?.centre === 'number' &&
      (level.badgeDesign.value === '' || level.badgeDesign.value === level.threshold);
    const value = threshold.replace(/\D/g, '').slice(0, 4);
    setLevel(level.key, {
      threshold,
      badgeDesign: syncValue && level.badgeDesign ? { ...level.badgeDesign, value } : level.badgeDesign,
    });
  };

  const removeLevel = (key: string) => {
    setDraft((prev) => ({ ...prev, levels: prev.levels.filter((level) => level.key !== key) }));
    setRemovingKey(undefined);
  };

  const handleSave = () => {
    const message = validateDraft(draft);
    setError(message ?? undefined);
    if (!message) onSave(draft);
  };

  const switchRow = (
    title: string,
    description: string,
    checked: boolean,
    onChange: (v: boolean) => void,
    disabled = false
  ) => (
    <Box className={classes.switchRow} style={{ opacity: disabled ? 0.55 : 1 }}>
      <Switch color='primary' checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <Box ml={1} mt='6px'>
        <Typography className={classes.switchTitle}>{title}</Typography>
        <Typography className={classes.switchDescription}>{description}</Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} disableBackdropClick maxWidth='md' fullWidth fullScreen={isMobile}>
        <DialogTitle style={{ paddingBottom: 0 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography className={classes.title}>
              {draft.achievementId ? 'Edit Achievement' : 'Add Achievement'}
            </Typography>
            <IconButton aria-label='Close' color='inherit' onClick={onClose}>
              <Close color='action' />
            </IconButton>
          </Box>
          <Box pr={3} mt={1}>
            <Typography className={classes.subtitle} color='textSecondary'>
              Set how the achievement is earned, then choose what each level awards.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Box className={classes.sections}>
            <Box>
              <Typography className={classes.sectionLabel}>Details</Typography>
              <TextField
                fullWidth
                variant='outlined'
                label='Achievement name'
                value={draft.name}
                onChange={(e) => set({ name: e.target.value })}
                className={classes.field}
              />
              <FormControl fullWidth variant='outlined' className={classes.field}>
                <InputLabel>Earned by</InputLabel>
                <Select
                  value={draft.metric}
                  onChange={(e) => onMetricChange(e.target.value as AchievementMetric)}
                  input={<OutlinedInput label='Earned by' />}
                >
                  {METRIC_OPTIONS.map((metric) => (
                    <MenuItem key={metric} value={metric}>
                      {METRICS[metric].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {draft.metric === 'report_field_total' && (
                <FormControl fullWidth variant='outlined' className={classes.field}>
                  <InputLabel>Report field</InputLabel>
                  <Select
                    value={draft.reportFieldId}
                    onChange={(e) => set({ reportFieldId: e.target.value as string })}
                    input={<OutlinedInput label='Report field' />}
                  >
                    {reportFields.map((field) => (
                      <MenuItem key={field.fieldId} value={field.fieldId}>
                        {field.name} ({field.reportTypeName})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                variant='outlined'
                label='Description'
                value={draft.description}
                onChange={(e) => set({ description: e.target.value })}
                multiline
                rows={2}
                helperText='Shown to volunteers on their achievements'
              />
            </Box>

            <AlertCard variant='info' title={draft.name || 'This achievement'}>
              <Typography style={{ fontSize: '14px', color: infoColours.darkTextColor }}>{summary}</Typography>
              {draft.metric === 'hours' && (
                <Typography style={{ fontSize: '14px', color: infoColours.darkTextColor, marginTop: '4px' }}>
                  Uses check-in and check-out time where recorded, otherwise session report times, plus individual
                  reports and additional hours on the profile.
                </Typography>
              )}
              {draft.metric === 'report_field_total' && (
                <Typography style={{ fontSize: '14px', color: infoColours.darkTextColor, marginTop: '4px' }}>
                  General session reports credit every attendee with the full value. Individual reports only count for
                  the volunteer they belong to.
                </Typography>
              )}
            </AlertCard>

            <Box>
              <Typography className={classes.sectionLabel}>Levels</Typography>
              <Typography className={classes.sectionHelp}>
                A level can award a badge, a certificate, both or neither. A level goes live when it is saved, and only
                volunteers who reach it after that are awarded automatically.
              </Typography>
              {draft.levels.map((level) => (
                <Box key={level.key} className={classes.level}>
                  <ButtonBase
                    className={classes.badgeButton}
                    onClick={() => setBadgeLevelKey(level.key)}
                    aria-label={`${level.badgeDesign ? 'Edit' : 'Add'} badge for ${level.name || 'this level'}`}
                  >
                    {level.badgeDesign ? (
                      <Badge design={level.badgeDesign} size={52} label={level.name} />
                    ) : (
                      <Box className={classes.noBadge}>
                        <Add fontSize='small' />
                      </Box>
                    )}
                  </ButtonBase>
                  <Box className={classes.levelFields}>
                    <Box>
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        label='Level name'
                        value={level.name}
                        onChange={(e) => setLevel(level.key, { name: e.target.value })}
                      />
                      <Typography className={classes.levelMeta}>
                        {level.liveFrom ? `Live from ${formatDate(level.liveFrom)}` : 'Goes live when saved'}
                      </Typography>
                    </Box>
                    {thresholdBased ? (
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        type='number'
                        label={`Threshold${unit ? ` (${unit})` : ''}`}
                        value={level.threshold}
                        onChange={(e) => onThresholdChange(level, e.target.value)}
                        inputProps={{ min: 0 }}
                      />
                    ) : (
                      <Typography className={classes.manualNote}>Awarded manually</Typography>
                    )}
                    <FormControl fullWidth variant='outlined' size='small'>
                      <InputLabel shrink>Certificate</InputLabel>
                      <Select
                        displayEmpty
                        value={level.certificateTemplateId}
                        onChange={(e) => setLevel(level.key, { certificateTemplateId: e.target.value as string })}
                        input={<OutlinedInput notched label='Certificate' />}
                      >
                        <MenuItem value=''>No certificate</MenuItem>
                        {certificateTemplates.map((template) => (
                          <MenuItem key={template.certificateTemplateId} value={template.certificateTemplateId}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box className={classes.levelActions}>
                    <TabletButton variant='text' color='primary' onClick={() => setBadgeLevelKey(level.key)}>
                      {level.badgeDesign ? 'Edit badge' : 'Add badge'}
                    </TabletButton>
                    <IconButton
                      size='small'
                      aria-label='Remove level'
                      disabled={draft.levels.length === 1}
                      onClick={() => (level.achievementLevelId ? setRemovingKey(level.key) : removeLevel(level.key))}
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              <Box borderTop='1px solid #EFEFEF' pt={1}>
                <TabletButton
                  variant='text'
                  color='primary'
                  startIcon={<Add />}
                  onClick={() => set({ levels: [...draft.levels, newLevel()] })}
                >
                  Add level
                </TabletButton>
              </Box>
            </Box>

            <Box>
              <Typography className={classes.sectionLabel}>Volunteer view</Typography>
              {switchRow(
                'Enabled',
                'Volunteers can earn this achievement and see it on their profile',
                draft.isEnabled,
                (isEnabled) => set({ isEnabled })
              )}
              {switchRow(
                'Show before earned',
                'Volunteers see locked levels they have not earned yet',
                draft.isDiscoverable,
                (isDiscoverable) => set({ isDiscoverable })
              )}
              {switchRow(
                'Show progress',
                'Volunteers see how close they are to the next level',
                thresholdBased && draft.showProgress,
                (showProgress) => set({ showProgress }),
                !thresholdBased
              )}
            </Box>

            <Box>
              <Typography className={classes.sectionLabel}>Award email message</Typography>
              <TextField
                fullWidth
                variant='outlined'
                label='Message'
                value={draft.customMessage}
                onChange={(e) => set({ customMessage: e.target.value })}
                multiline
                rows={3}
                helperText='Added to the Achievement Awarded email (Communications, Templates) wherever {{ACHIEVEMENT_MESSAGE}} is used'
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          {error && <Typography className={classes.error}>{error}</Typography>}
          <TabletButton variant='text' color='error' onClick={onClose} disabled={saving} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </TabletButton>
        </DialogActions>
      </Dialog>

      {badgeLevel && (
        <BadgeBuilderDialog
          open
          title={badgeLevel.name ? `Badge for ${badgeLevel.name}` : 'Badge'}
          design={
            badgeLevel.badgeDesign ?? defaultBadge(draft.metric, draft.levels.indexOf(badgeLevel), badgeLevel.threshold)
          }
          onClose={() => setBadgeLevelKey(undefined)}
          onRemove={
            badgeLevel.badgeDesign
              ? () => {
                  setLevel(badgeLevel.key, { badgeDesign: null });
                  setBadgeLevelKey(undefined);
                }
              : undefined
          }
          onSave={(badgeDesign: BadgeDesign) => {
            setLevel(badgeLevel.key, { badgeDesign });
            setBadgeLevelKey(undefined);
          }}
        />
      )}

      <ConfirmationDialog
        title='Remove level'
        body={`Removing "${removingLevel?.name ??
          'this level'}" also removes it from every volunteer who has earned it. This happens when you save.`}
        open={Boolean(removingLevel)}
        closeActionText='Cancel'
        confirmActionText='Remove'
        handleCloseClick={() => setRemovingKey(undefined)}
        handleConfirmClick={() => removingKey && removeLevel(removingKey)}
      />
    </>
  );
};
